import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { workOrderService, STATUS_FLOW } from '../../services/workOrderService';
import { customerService } from '../../services/customerService';
import { staffService } from '../../services/staffService';
import StatusBadge from '../../components/StatusBadge';
import { FiPlus, FiEdit, FiTrash2, FiEye } from 'react-icons/fi';

const WorkOrderList = () => {
  const [workOrders, setWorkOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [staff, setStaff] = useState([]);
  const [filter, setFilter] = useState('all'); 
  const [paymentFilter, setPaymentFilter] = useState('all');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [woData, customerData, staffData] = await Promise.all([
        workOrderService.getAll(),
        customerService.getAll(),
        staffService.getAll()
      ]);
      
      setWorkOrders(woData || []);
      setCustomers(customerData || []);
      setStaff(staffData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this work order?')) {
      try {
        await workOrderService.delete(id);
        await loadData();
      } catch (error) {
        alert('Error deleting work order: ' + error.message);
      }
    }
  };

  const getCustomerName = (id) => {
    const customer = customers.find(c => c.id === id);
    return customer ? customer.name : '-';
  };

  const getStaffName = (id) => {
    const staffMember = staff.find(s => s.id === id);
    return staffMember ? staffMember.name : '-';
  };

  // Buat opsi filter dari STATUS_FLOW
  const getStatusOptions = () => {
    const options = [
      { value: 'all', label: 'Semua' }
    ];
    
    // Ambil dari STATUS_FLOW
    Object.keys(STATUS_FLOW || {}).forEach(key => {
      options.push({
        value: key,
        label: STATUS_FLOW[key].label
      });
    });
    
    return options;
  };

  // Make sure workOrders is an array before filtering
  const filteredOrders = Array.isArray(workOrders) 
    ? workOrders.filter(wo => {
        const matchStatus = filter === 'all' || wo.status === filter;
        const matchPayment = paymentFilter === 'all' || wo.paymentStatus === paymentFilter;
        return matchStatus && matchPayment;
      })
    : [];

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-[var(--text-secondary)]">Loading work orders...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-[var(--text-primary)]">Work Orders</h1>
        <Link
          to="/work-orders/new"
          className="flex items-center space-x-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
        >
          <FiPlus size={20} />
          <span>New Work Order</span>
        </Link>
      </div>

      {/* Filter - Dropdown */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-[var(--text-secondary)]">Status WO</label>
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            {getStatusOptions().map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <label className="text-xs font-medium text-[var(--text-secondary)]">Pembayaran</label>
          <select
            value={paymentFilter}
            onChange={(e) => setPaymentFilter(e.target.value)}
            className="px-3 py-1.5 text-sm border border-[var(--border-color)] rounded-lg bg-[var(--bg-primary)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">Semua</option>
            <option value="paid">Lunas</option>
            <option value="pending">Belum Bayar</option>
          </select>
        </div>

        {/* Info jumlah */}
        <span className="text-xs text-[var(--text-secondary)] ml-auto">
          {filteredOrders.length} WO
        </span>
      </div>

      <div className="bg-[var(--bg-primary)] rounded-xl shadow-[var(--shadow-sm)] border border-[var(--border-color)] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-[var(--bg-secondary)]">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  WO Number
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Project
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Customer
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Staff
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Status
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Pembayaran
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Target
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Files
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-[var(--text-secondary)]">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredOrders.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-4 py-6 text-center text-[var(--text-secondary)]">
                    Tidak ada Work Order
                  </td>
                </tr>
              ) : (
                filteredOrders.map((order) => (
                  <tr key={order.id} className="border-t border-[var(--border-color)] hover:bg-[var(--bg-secondary)] transition-colors">
                    <td className="px-4 py-3 text-sm font-medium text-[var(--text-primary)]">
                      {order.wo_number || order.woNumber}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-primary)]">
                      {order.project_name || order.projectName}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                      {getCustomerName(order.customer_id || order.customerId)}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                      {getStaffName(order.staff_id || order.staffId)}
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={order.status} size="sm" />
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        order.paymentStatus === 'paid' ? 'bg-green-100 text-green-800' :
                        order.paymentStatus === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.paymentStatus === 'paid' ? 'Lunas' :
                         order.paymentStatus === 'pending' ? 'Pending' :
                         '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                      {order.target_date || order.targetDate || '-'}
                    </td>
                    <td className="px-4 py-3 text-sm text-[var(--text-secondary)]">
                      {order.files && order.files.length > 0 ? (
                        <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                          {order.files.length}
                        </span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <Link
                          to={`/work-orders/${order.id}`}
                          className="p-1.5 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Detail"
                        >
                          <FiEye size={18} />
                        </Link>
                        <Link
                          to={`/work-orders/edit/${order.id}`}
                          className="p-1.5 text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50 rounded-lg transition-colors"
                          title="Edit"
                        >
                          <FiEdit size={18} />
                        </Link>
                        <button
                          onClick={() => handleDelete(order.id)}
                          className="p-1.5 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors"
                          title="Hapus"
                        >
                          <FiTrash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default WorkOrderList;